import React, { useEffect, useState, useRef, useCallback } from "react"
import UserSelector from "./components/UserSelector"
import FlightSearchPanel from "./components/FlightSearchPanel"
import { FlightSidebar } from "./components/Sidebar"
import { PricingPanel } from "./components/PricingPanel"
import {
  useGetFlightDatesMutation,
  useGetFligthTransportToursMutation,
  useLazyGetTerminalsQuery,
  useLazyGetTransportPriceCalendarQuery,
} from "../../../app/services/book/filters"
import FlightTicketCard from "./components/FlightTicketCard/FlightTicketCard"
import Modal from "./components/Modal"
import FlightCard from "./components/FlightCard"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import {
  selectAviaCountryById,
  selectAviaSearchAirlinesCodes,
  selectAviaSearchCompanies,
  selectAviaSearchCurrency,
  selectAviaSearchDates,
  selectAviaSearchPassengers,
  selectAviaPriceCalendarEnabled,
  selectAviaSearchResult,
  selectAviaSearchTerminals,
  selectAviaSearchUsers,
  selectAviaSidebarFilters,
  selectSelectedAviaTerminals,
  setAviaSearchParams,
} from "../../../app/reducers/book/aviaSearchSlice"
import { useGetAviaFilterDataQuery } from "../../../app/services/book/filter"
import {
  buildChildrenArray,
  collectIds,
  getAllTerminalIds,
  mergeDefaultIds,
} from "./utils/processing/search"
import { CURRENCIES } from "../../../variables/common"
import BulletPointIcn from "../../../assets/avia/ticket/bullet-point.svg"
import { FlightDetailsSkeleton } from "./components/FlightTicketCard"
import { PlanePreloader } from "./components/PlanePreloader"
import { AnimatePresence } from "framer-motion"
import { skipToken } from "@reduxjs/toolkit/query/react"
import { getAllTerminalsWithSelection } from "./utils/filters/serch"
import { LayoutGrid } from "lucide-react"
import { FLIGHT_DURATION_VARIABLES, PRICE_VARIABLES } from "./utils/variables/search"
import { useGetCompaniesQuery } from "../../../app/services/transport/companies"
import { fetchAirlines, fetchAirlinesByIata } from "../../../shared/api/airFetcher"
import { calculateNights, normalizeDates } from "./utils/date/common"
import { saveSearchToHistory } from "./utils/localstorage/search"
import SearchTabs from "../../../shared/components/SearchTabs"

const COUNTREIS_IDS_KEY_PARAM = "countriesIds"
const PER_PAGE = 5

const defaultSidebarFilters = {
  price: {
    min: PRICE_VARIABLES.DEFAULT_MIN,
    max: PRICE_VARIABLES.DEFAULT_MAX,
  },
  [FLIGHT_DURATION_VARIABLES.PARAM_KEY]: {
    min: FLIGHT_DURATION_VARIABLES.DEFAULT_MIN,
    max: FLIGHT_DURATION_VARIABLES.DEFAULT_MAX,
  },
  [COUNTREIS_IDS_KEY_PARAM]: [],
  airlinesCodes: [],
  terminalIds: [],
}

const PRICE_CALENDAR_MONTHS = 12

const toIsoDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`

const computeIsLocal = (departure) => {
  return (departure || []).some((item) => item?.is_local)
}

const App = (props) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  const dispatch = useDispatch()

  // selectors
  const searchRes = useSelector(selectAviaSearchResult)
  const {
    arrivalTerminals,
    departureTerminals,
    defaultDeparture,
    defaultArrival,
    isLocal,
    isSwap,
  } = useSelector(selectAviaSearchTerminals)

  const [sidebarFilters, setSidebarFilters] = useState(defaultSidebarFilters)

  const { selectedDeparture, selectedArrival } = useSelector(selectSelectedAviaTerminals)

  const listUsers = useSelector(selectAviaSearchUsers)

  const { children, adults, infants } = useSelector(selectAviaSearchPassengers)

  const { from, to } = useSelector(selectAviaSearchDates)
  const currency = useSelector(selectAviaSearchCurrency)
  const priceCalendarEnabled = useSelector(selectAviaPriceCalendarEnabled)

  const airlinesCodes = useSelector(selectAviaSearchAirlinesCodes)

  const selectedCurCode = CURRENCIES.find((item) => item.code === currency).code

  // local states
  const { isAdmin, currentUserId } = props

  const [selectedUserId, setSelectedUserId] = useState(currentUserId)
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [selectedCards, setSelectedCards] = useState([])
  const [hasSearched, setHasSearched] = useState(false)
  const [airlines, setAirlines] = useState([])
  const [isMainSearchLoading, setIsMainSearchLoading] = useState(false)
  const [shouldShowSidebar, setShouldShowSidebar] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const [searchKey, setSearchKey] = useState(0)
  const [searchPassengers, setSearchPassengers] = useState(null)

  const observerTarget = useRef(null)

  // services
  const [getFlightDates, {}] = useGetFlightDatesMutation()
  const [fetchTerminals] = useLazyGetTerminalsQuery()
  const [fetchPriceCalendar] = useLazyGetTransportPriceCalendarQuery()

  const [searchTours, { isLoading: isLoadingTourse }] = useGetFligthTransportToursMutation()

  // services
  useGetAviaFilterDataQuery(locale ? { local: locale } : skipToken)

  useGetCompaniesQuery()

  useEffect(() => {
    if (!hasSearched) return

    setCurrentPage(1)
    setIsFilterLoading(true)
    const timeout = setTimeout(() => {
      handleSearch({ isFilterUpdate: true })
    }, 400)

    return () => clearTimeout(timeout)
  }, [sidebarFilters])

  useEffect(() => {
    fetchAirlinesByIata(airlinesCodes).then((data) => {
      setAirlines(data)
    })
  }, [airlinesCodes])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoadingTourse) {
          loadMoreResults()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [hasMore, isLoadingMore, isLoadingTourse])

  const handleModal = (cards) => {
    setSelectedCards(isOpenModal ? null : cards)
    setIsOpenModal(!isOpenModal)
  }

  const loadMoreResults = async () => {
    if (isLoadingMore || !hasMore) return

    setIsLoadingMore(true)
    const nextPage = currentPage + 1

    try {
      await handleSearch({ isFilterUpdate: true, page: nextPage, append: true })
      setCurrentPage(nextPage)
    } catch (error) {
      console.log("Load more error:", error)
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Цены под датами в календаре: минимум за 1 взрослого по направлению, без привязки
  // к провайдеру и авиакомпании. Показываем только после первого поиска.
  const loadPriceCalendar = (departureIds, arrivalIds) => {
    if (!priceCalendarEnabled || departureIds.length === 0 || arrivalIds.length === 0) return

    const today = new Date()
    const dateFrom = toIsoDate(today)
    const dateTo = toIsoDate(
      new Date(today.getFullYear(), today.getMonth() + PRICE_CALENDAR_MONTHS + 1, 0)
    )

    fetchPriceCalendar({
      leg: "depart",
      fromTerminalIds: departureIds,
      toTerminalIds: arrivalIds,
      dateFrom,
      dateTo,
    })
    fetchPriceCalendar({
      leg: "retur",
      fromTerminalIds: arrivalIds,
      toTerminalIds: departureIds,
      dateFrom,
      dateTo,
    })
  }

  const handleSearch = async ({
    newCurrency,
    isMainSearch,
    isFilterUpdate,
    page = 1,
    append = false,
  }) => {
    if (isMainSearch) {
      setSearchKey((prev) => prev + 1)
      setSidebarFilters(defaultSidebarFilters)
      setSearchPassengers({ children, adults, infants })
    }

    const { price, flightDuration, departureHours, airlinesCodes } = sidebarFilters

    if (newCurrency && (!hasSearched || searchRes.length === 0)) return

    const normalizedFrom = normalizeDates(from)
    const normalizedTo = normalizeDates(to)

    if (newCurrency) {
      setIsFilterLoading(true)
    }

    if (isMainSearch) {
      setIsMainSearchLoading(true)
      setCurrentPage(1)
      saveSearchToHistory(selectedDeparture, selectedArrival, normalizedFrom, normalizedTo)
    }

    const rawDepartureIds = collectIds(selectedDeparture)
    const rawArrivalIds = collectIds(selectedArrival)

    const isLocalComputed = computeIsLocal(selectedDeparture)

    const departureIds = isLocalComputed ? rawDepartureIds : rawArrivalIds
    const baseArrivalIds = isLocalComputed ? rawArrivalIds : rawDepartureIds

    const arrivalIds =
      sidebarFilters.terminalIds?.length > 0 ? sidebarFilters.terminalIds : baseArrivalIds

    const cleanDepartureIds = departureIds.filter((id) => !arrivalIds.includes(id))

    if (
      (cleanDepartureIds.length > 0 || arrivalIds.length > 0) &&
      (normalizedFrom || normalizedTo)
    ) {
      let periodType,
        fromDate,
        toDate,
        nights = 0
      let fromNights, toNights

      fromDate = Array.isArray(normalizedFrom) ? normalizedFrom[0] : normalizedFrom

      const hasMultipleDepartures = Array.isArray(normalizedFrom) && normalizedFrom.length > 1
      const hasReturn = !!normalizedTo

      if (hasMultipleDepartures) {
        toDate = normalizedFrom[normalizedFrom.length - 1]
        nights = calculateNights(fromDate, toDate)
        periodType = "range"
      } else if (hasReturn) {
        toDate = Array.isArray(normalizedTo) ? normalizedTo[normalizedTo.length - 1] : normalizedTo
        periodType = "fix"
      } else {
        toDate = fromDate
        fromNights = 1
        toNights = 10
        periodType = "fix"
      }

      const params = {
        period_type: periodType,
        from_date: fromDate,
        to_date: toDate || fromDate,
        depart_terminal_ids: cleanDepartureIds,
        arrival_terminal_ids: arrivalIds,
        check_depart: isLocalComputed ? 1 : 0,
        check_retur: !isLocalComputed ? 1 : hasReturn ? 1 : 0,
        adults: adults,
        is_transfer: 0,
        type_filter: "airbus",
        locale: locale,
        currency: newCurrency || selectedCurCode,
        user_id: selectedUserId,
        format: "json",
        page: page,
        per_page: PER_PAGE,
      }

      if (nights > 0) params.nights = nights
      if (fromNights !== undefined) params.from_nights = fromNights
      if (toNights !== undefined) params.to_nights = toNights

      if (flightDuration.min > FLIGHT_DURATION_VARIABLES.DEFAULT_MIN) {
        params.min_flight_duration = flightDuration.min
      }
      if (flightDuration.max < FLIGHT_DURATION_VARIABLES.DEFAULT_MAX) {
        params.max_flight_duration = flightDuration.max
      }

      if (price.min > PRICE_VARIABLES.DEFAULT_MIN) params.min_price = price.min
      if (price.max < PRICE_VARIABLES.DEFAULT_MAX) params.max_price = price.max

      if (airlinesCodes?.length > 0) {
        params.airlines = airlinesCodes.join(",")
      }

      const childrenArr = buildChildrenArray(children, infants)
      if (childrenArr.length > 0) {
        params.children = childrenArr
      }

      setHasSearched(true)

      try {
        const result = await searchTours(params).unwrap()

        if (result.pagination) {
          setHasMore(result.pagination.has_more)
        } else {
          setHasMore(false)
        }

        setShouldShowSidebar(true)

        if (isMainSearch) {
          loadPriceCalendar(cleanDepartureIds, baseArrivalIds)
        }
      } catch (error) {
        console.log("get tickets err: ", error)
        setHasMore(false)
      } finally {
        if (isMainSearch) {
          setIsMainSearchLoading(false)
        }
        setIsFilterLoading(false)
      }
    }
  }

  const handleDates = async () => {
    const rawDepartureIds = collectIds(selectedDeparture)
    const rawArrivalIds = collectIds(selectedArrival)

    const isLocalComputed = computeIsLocal(selectedDeparture)

    const departureIds = isLocalComputed ? rawDepartureIds : rawArrivalIds

    const arrivalIds = isLocalComputed ? rawArrivalIds : rawDepartureIds

    if (hasSearched) {
      loadPriceCalendar(
        departureIds.filter((id) => !arrivalIds.includes(id)),
        arrivalIds
      )
    }

    if (departureIds.length > 0 || arrivalIds.length > 0) {
      try {
        await getFlightDates({
          departTerminalIds: departureIds,
          arrivalTerminalIds: arrivalIds,
          locale: locale,
        })
      } catch (error) {
        console.log("get flight dates err: ", error)
      }
    }
  }

  const handleTerminals = async ({ terminalId }) => {
    const selectedArrIds = collectIds(selectedArrival)
    const departureIds = collectIds(selectedDeparture)
    const defaultDepartureIds = defaultDeparture?.ids ?? []

    let idsToFetch = [...departureIds]

    if (terminalId) {
      const alreadySelected = idsToFetch.includes(terminalId)

      if (alreadySelected) {
        idsToFetch = idsToFetch.filter((id) => id !== terminalId)

        if (idsToFetch.length === 0) {
          idsToFetch = [...defaultDepartureIds]
        }
      } else {
        idsToFetch.push(terminalId)
      }
    }

    if (idsToFetch.length === 0) {
      return
    }

    try {
      const resp = await fetchTerminals({
        ids: idsToFetch,
        local: locale,
      }).unwrap()

      const modifyTerminals = getAllTerminalsWithSelection(resp, selectedArrIds)

      dispatch(
        setAviaSearchParams({
          key: "terminals.arrivalTerminals",
          value: modifyTerminals,
        })
      )
    } catch (error) {
      console.log("fetch terminals error: ", error)
    }
  }

  const handleSidebarFilterChange = ({ key, value, overwrite = false } = {}) => {
    setSidebarFilters((prev) => {
      const keys = key.split(".")
      const newState = { ...prev }

      let current = newState

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i]
        current[k] = typeof current[k] === "object" && current[k] !== null ? { ...current[k] } : {}
        current = current[k]
      }

      const lastKey = keys[keys.length - 1]
      let prevValue = current[lastKey]

      if (overwrite) {
        current[lastKey] = value
      } else if (Array.isArray(value)) {
        current[lastKey] = value
      } else if (Array.isArray(prevValue)) {
        current[lastKey] = prevValue.includes(value)
          ? prevValue.filter((v) => v !== value)
          : [...prevValue, value]
      } else if (prevValue === undefined || prevValue === null) {
        current[lastKey] = [value]
      } else {
        current[lastKey] = [prevValue, value]
      }

      return newState
    })
  }

  const handleResetSidebarFilters = ({ resetCountries, resetAirports }) => {
    setSidebarFilters(defaultSidebarFilters)
    setCurrentPage(1)
    resetAirports()
    resetCountries()
  }

  return (
    <div className="bg-second-white flex flex-col gap-2">
      <div className="relative flex flex-col pb-1">
        <SearchTabs active={props?.activeTab ?? "avia"} />

        <FlightSearchPanel
          handleSearch={handleSearch}
          getFlightDates={getFlightDates}
          handleDates={handleDates}
          searchTours={searchTours}
          arrivalTerminals={arrivalTerminals}
          departureTerminals={departureTerminals}
          defaultDeparture={defaultDeparture}
          defaultArrival={defaultArrival}
          children={children}
          adults={adults}
          infants={infants}
          selectedArrival={selectedArrival}
          selectedDeparture={selectedDeparture}
          fetchTerminals={handleTerminals}
          isMainSearchLoading={isMainSearchLoading}
          setShouldShowSidebar={setShouldShowSidebar}
        />

        {isAdmin && (
          <UserSelector
            users={listUsers}
            onChange={(id) => {
              setSelectedUserId((prev) => (prev === id ? null : id))
            }}
            selectedUserId={selectedUserId}
            className="absolute top-0 right-0"
          />
        )}
      </div>

      <AnimatePresence>{isMainSearchLoading && <PlanePreloader />}</AnimatePresence>

      <div className="flex gap-[18px] pt-3">
        {(shouldShowSidebar || isMainSearchLoading) && (
          <FlightSidebar
            key={searchKey}
            filters={sidebarFilters}
            onChange={handleSidebarFilterChange}
            handleResetFilters={handleResetSidebarFilters}
            isLoading={isMainSearchLoading}
            airlines={airlines}
          />
        )}

        <div className="w-full flex flex-col gap-4">
          {(isLoadingTourse && currentPage === 1) || isFilterLoading ? (
            [1, 2, 3].map((_, index) => <FlightDetailsSkeleton key={index} />)
          ) : searchRes?.length > 0 ? (
            <>
              {searchRes.map((item, index) => (
                <FlightTicketCard
                  key={item.id || `${item.arrival_terminal_code}-${index}`}
                  item={item}
                  currency={selectedCurCode}
                  onClick={handleModal}
                  airlines={airlines}
                  passengers={searchPassengers}
                />
              ))}

              {isLoadingMore && (
                <div className="flex justify-center py-4">
                  <FlightDetailsSkeleton />
                </div>
              )}

              {hasMore && <div ref={observerTarget} className="h-4" />}
            </>
          ) : (
            hasSearched && (
              <p className="font-semibold pt-4 text-light-gray text-center text-3xl">
                {t("admin.no_tickets_found")}
              </p>
            )
          )}
        </div>
      </div>

      <Modal
        isOpen={isOpenModal}
        title={t("admin.flug_details")}
        onClose={() => setIsOpenModal(false)}
      >
        <div className="w-fit p-1 bg-second-success/20 border-[1px] rounded-md border-second-success mb-3 flex items-center gap-2 text-lg">
          <LayoutGrid className="text-second-success" />
          <span className="text-second-success font-medium">{t("admin.tariff_rules")}</span>
        </div>

        <div className="flex flex-col gap-5">
          {selectedCards?.map((card) => (
            <FlightCard isRoundTrip={selectedCards.length > 1} key={card.type} cardData={card} />
          ))}
        </div>
      </Modal>
    </div>
  )
}

export default App
